import { FC } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Redirect } from 'react-router-dom';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Box, Grid, useTheme } from '@mui/material';
import { homeUrl } from '@utils/UrlBuilder';
import { CommonLoader } from '@components/index';
import strings from '@strings/LocalisedStrings';
import desktop1 from '@images/desktop1.png';
import desktop2 from '@images/desktop2.png';
import mobile1 from '@images/mobile1.png';
import mobile2 from '@images/mobile2.png';
import mobile3 from '@images/mobile3.png';

const Home: FC = (): JSX.Element => {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

  const theme = useTheme();

  const handleLoginPressed = async () => {
    await loginWithRedirect();
  };

  if (isLoading) return <CommonLoader />;
  if (isAuthenticated) return <Redirect to={homeUrl()} />;
  return (
    <Container>
      <Grid container spacing={2} direction="column" mb={4}>
        <Grid item>
          <Typography fontWeight="bold" variant="h4">
            Your Client Portal
          </Typography>
        </Grid>

        <Grid item>
          <Typography variant="body1" component="p">
            {strings.dashClientPortal}
          </Typography>
        </Grid>

        <Grid item>
          <Typography variant="h6" color={theme.palette.primary.main} fontWeight={700}>
            Features
          </Typography>
        </Grid>

        <Grid item>
          <Typography variant="body1" component="p">
            {strings.dashClientPortalDesc}
          </Typography>
        </Grid>

        <Grid item container spacing={{ xs: 2, md: 3 }} columns={{ xs: 2, sm: 4, md: 12 }} pt="20px">
          <Grid item xs={2} sm={6} md={6} textAlign="center">
            <img src={desktop1} alt="desktop1" width={350} />
            <Typography variant="body1" component="p" textAlign="center" pt="20px">
              Detailed portfolio charting allows you to customise the date range that you are reviewing as well as comparing portfolios to selected
              benchmarks.
            </Typography>
          </Grid>
          <Grid item xs={2} sm={6} md={6} textAlign="center">
            <img src={desktop2} alt="desktop2" width={350} />
            <Typography variant="body1" component="p" textAlign="center" paddingTop="20px">
              Easily search, filter, sort and download your holdings and transactions data.
            </Typography>
          </Grid>
        </Grid>

        <Grid item mt={4}>
          <Typography variant="body1" component="p">
            The Client Portal is also fully responsive and provides up-to-date secure portfolio access on your mobile device. Your Super and
            Investment accounts, portfolio performance, holdings, and transactions are available at your fingertips.
          </Typography>
        </Grid>

        <Grid item container spacing={{ xs: 2, md: 3 }} pt="20px" columns={{ xs: 2, sm: 4, md: 12 }}>
          <Grid item xs={2} sm={4} md={4} textAlign="center">
            <img src={mobile1} alt="mobile1" width={96} />
            <Typography variant="body1" component="p" textAlign="center">
              Access a snapshot of your account across all portfolios
            </Typography>
          </Grid>
          <Grid item xs={2} sm={4} md={4} textAlign="center">
            <img src={mobile2} alt="mobile2" width={96} />
            <Typography variant="body1" component="p" textAlign="center">
              Per portfolio performance and asset valuations
            </Typography>
          </Grid>
          <Grid item xs={2} sm={4} md={4} textAlign="center">
            <img src={mobile3} alt="mobile3" width={96} />
            <Typography variant="body1" component="p" textAlign="center">
              Deep dive into an asset cost base and recent transactions
            </Typography>
          </Grid>
        </Grid>

        <Grid item mt={4}>
          <Typography variant="h6" color={theme.palette.primary.main} fontWeight={700}>
            Access
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1" component="p">
            Access the Client Portal is available in a few easy steps:
          </Typography>
        </Grid>

        <Grid item>
          <Box component="ol" marginLeft="16px">
            <li>
              <Typography variant="body1" component="p">
                Have your adviser create your log in (if you don’t already have one). Your financial adviser can initiate the registration process.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" component="p">
                Complete the registration process by following the instructions in your welcome email and creating a new password.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" component="p">
                Go to portal.dash.com.au on your device
              </Typography>
            </li>
            <li>
              <Typography variant="body1" component="p">
                Login to the portal using your registered email and password.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" component="p">
                Enter the One Time Passcode (OTP) that is sent to your mobile device for Multi Factor Authentication
              </Typography>
            </li>
          </Box>
        </Grid>

        <Grid item mt={6} alignSelf="center">
          <Button
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              ':hover': {
                filter: `brightness(90%)`,
                backgroundColor: theme.palette.primary.main,
              },
            }}
            onClick={handleLoginPressed}
            variant="contained"
          >
            {strings.login}
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Home;
